/**
 * API Service
 * 
 * Handles all communication with the BringIQ backend API.
 * Supports both regular requests and Server-Sent Events (SSE) for streaming.
 */

import type { Skill, CreateSkillRequest, UpdateSkillRequest } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
const AUTH_EXPIRED_EVENT = 'bringiq:auth-expired';

// Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Message {
  id: string;
  chatId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  toolCalls?: ToolCall[];
}

export interface ToolCall {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result?: string;
}

export interface Chat {
  id: string;
  title: string;
  assistantId?: string;
  userId: string;
  messageCount: number;
  isPinned?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Assistant {
  id: string;
  name: string;
  description: string;
  systemInstructions?: string;
  llmModel?: string;
  icon?: string;
  status: string;
}

export interface AIStatus {
  ready: boolean;
  model: string;
  configured: boolean;
  apiUrl: string;
}

export interface AIModel {
  id: string;
  name: string;
  description: string;
  publisher: string;
  provider: string;
  capabilities?: {
    chat: boolean;
    streaming: boolean;
    toolCalling: boolean;
    vision?: boolean;
  };
}

export interface AIModelsResponse {
  total: number;
  providers: string[];
  models: AIModel[];
}

export interface StreamChunk {
  type: 'content' | 'tool_call' | 'done' | 'error';
  data?: string;
  error?: string;
}

/** Chunk shape for multi-model comparison streaming */
export interface MultiModelStreamChunk {
  type: 'content' | 'model_done' | 'error' | 'all_done';
  model: string;
  data?: string;
  error?: string;
  latencyMs?: number;
}

// RAG Types
export interface Datasource {
  id: string;
  assistantId: string;
  name: string;
  description?: string;
  type: 'file' | 'url' | 'github' | 'confluence';
  status: 'pending' | 'extracting' | 'embedding' | 'complete' | 'error';
  config: Record<string, unknown>;
  metadata?: {
    fileName?: string;
    fileType?: string;
    fileSize?: number;
    chunkCount?: number;
    embeddingModel?: string;
    lastProcessedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface IngestionProgress {
  stage: 'loading' | 'chunking' | 'embedding' | 'storing' | 'completed' | 'failed';
  progress: number;
  currentItem?: number;
  totalItems?: number;
  message: string;
}

export interface IngestionResult {
  datasourceId: string;
  documentId: string;
  totalChunks: number;
  successfulChunks: number;
  failedChunks: number;
  totalTokens: number;
  processingTime: number;
  status?: 'processing' | 'completed' | 'failed';
  jobId?: string;
  error?: string;
}

export interface RAGSearchResult {
  chunks: {
    id: string;
    content: string;
    score: number;
    documentName: string;
    metadata: {
      pageNumber?: number;
      sectionHeading?: string;
    };
  }[];
  totalRetrieved: number;
  processingTime: number;
}

export interface KnowledgeBaseStats {
  totalDocuments: number;
  totalChunks: number;
  datasources: {
    datasourceId: string;
    documentCount: number;
    chunkCount: number;
  }[];
}

// ===== Knowledge Hub: Question Preparation & Audit History =====
export type QuestionSource = 'ai' | 'manual';
export type QuestionStatus = 'draft' | 'pending_review' | 'approved' | 'rejected' | 'answered';
export type ReviewDecision = 'approved' | 'rejected';

export interface KnowledgeQuestion {
  id: string;
  datasourceId: string;
  organizationId: string;
  text: string;
  source: QuestionSource;
  status: QuestionStatus;
  stakeholder?: string;
  answer?: string;
  answeredBy?: string;
  answeredByEmail?: string;
  answeredAt?: string;
  answerScore?: number;
  answerScoreRationale?: string;
  scoredAt?: string;
  reviewedBy?: string;
  reviewedByEmail?: string;
  reviewedAt?: string;
  reviewDecision?: ReviewDecision;
  reviewNotes?: string;
  generationBatchId?: string;
  createdBy: string;
  createdByEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export type KnowledgeAuditAction =
  | 'document_added'
  | 'questions_generated'
  | 'question_created'
  | 'question_edited'
  | 'question_deleted'
  | 'question_answered'
  | 'question_reviewed'
  | 'questions_exported'
  | 'answers_uploaded'
  | 'document_scored'
  | 'added_to_rag';

export interface KnowledgeAuditEvent {
  id: string;
  datasourceId: string;
  organizationId: string;
  action: KnowledgeAuditAction;
  questionId?: string;
  actorId: string;
  actorEmail?: string;
  summary: string;
  details?: Record<string, unknown>;
  createdAt: string;
}

// Embedding / RAG Model Types
export interface EmbeddingModelInfo {
  provider: string;
  model: string;
  dimensions: number;
  maxTokens: number;
  pricePerMillionTokens: number;
  mtebScore: number;
  description: string;
  bestFor: string[];
  pros: string[];
  cons: string[];
}

export interface EmbeddingConfig {
  current: {
    useCase: string;
    provider: string;
    model: string;
    dimensions: number;
    usingDynamicProvider: boolean;
  };
  availableProviders: Record<string, boolean>;
  presets: Record<string, { provider: string; model: string; dimensions: number }>;
  models: EmbeddingModelInfo[];
}

// Helper to get auth token
function getAuthToken(): string {
  const storedToken = localStorage.getItem('bringiq_token');

  if (!storedToken) {
    return 'demo-token';
  }

  if (isJwtExpired(storedToken)) {
    handleAuthExpired();
    return 'demo-token';
  }

  return storedToken;
}

function handleAuthExpired(): void {
  localStorage.removeItem('bringiq_token');

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));

    // Dev/no-auth mode can continue with demo-token flow. In Auth0 mode,
    // AuthContext handles this event and signs the user out to /login.
    const hasAuth0Config = !!process.env.REACT_APP_AUTH0_DOMAIN && !!process.env.REACT_APP_AUTH0_CLIENT_ID;
    if (!hasAuth0Config && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  }
}

function isJwtExpired(token: string): boolean {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') {
      return false;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowInSeconds;
  } catch {
    return false;
  }
}

// Helper for API requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getAuthToken()}`,
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        handleAuthExpired();
      }
      throw new Error(data.error?.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

// API Methods
export const api = {
  // Health & Status
  async getHealth(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return apiRequest('/health');
  },

  async getAIStatus(): Promise<ApiResponse<AIStatus>> {
    return apiRequest('/ai/status');
  },

  // AI Models
  async getAIModels(provider?: string): Promise<ApiResponse<AIModelsResponse>> {
    const query = provider ? `?provider=${provider}` : '';
    return apiRequest(`/ai/models${query}`);
  },

  // AI Text Enhancement/Generation
  async enhanceWithAI(
    text: string,
    context: {
      type: 'system_instructions' | 'description' | 'conversation_starter';
      assistantName?: string;
      assistantDescription?: string;
    }
  ): Promise<ApiResponse<{ enhancedText: string }>> {
    return apiRequest('/ai/enhance', {
      method: 'POST',
      body: JSON.stringify({ text, context }),
    });
  },

  // Assistants
  async listAssistants(): Promise<PaginatedResponse<Assistant>> {
    return apiRequest('/assistants') as Promise<PaginatedResponse<Assistant>>;
  },

  async getAssistant(id: string): Promise<ApiResponse<Assistant>> {
    return apiRequest(`/assistants/${id}`);
  },

  async createAssistant(data: Partial<Assistant>): Promise<ApiResponse<Assistant>> {
    return apiRequest('/assistants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Create assistant with full parameters
  async createAssistantWithDatasources(data: {
    name: string;
    description: string;
    systemInstructions: string;
    llmModel: string;
    temperature?: number;
    categories?: string[];
    conversationStarters?: string[];
  }): Promise<ApiResponse<Assistant>> {
    return apiRequest('/assistants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAssistant(id: string, data: Partial<Assistant>): Promise<ApiResponse<Assistant>> {
    return apiRequest(`/assistants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteAssistant(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/assistants/${id}`, {
      method: 'DELETE',
    });
  },

  // Chats
  async listChats(): Promise<PaginatedResponse<Chat>> {
    return apiRequest('/chats') as Promise<PaginatedResponse<Chat>>;
  },

  async getGroupedChats(): Promise<ApiResponse<{
    ungrouped: Chat[];
    assistantChats: Record<string, { assistantId: string; assistantName?: string; chats: Chat[] }>;
  }>> {
    return apiRequest('/chats/grouped');
  },

  async getUngroupedChats(): Promise<ApiResponse<Chat[]>> {
    return apiRequest('/chats/ungrouped');
  },

  async getChat(id: string): Promise<ApiResponse<Chat>> {
    return apiRequest(`/chats/${id}`);
  },

  async createChat(assistantId?: string, title?: string): Promise<ApiResponse<Chat>> {
    return apiRequest('/chats', {
      method: 'POST',
      body: JSON.stringify({ assistantId, title }),
    });
  },

  async updateChat(id: string, updates: { title?: string; isPinned?: boolean }): Promise<ApiResponse<Chat>> {
    return apiRequest(`/chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async renameChat(id: string, title: string): Promise<ApiResponse<Chat>> {
    return apiRequest(`/chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  },

  async togglePinChat(id: string): Promise<ApiResponse<Chat>> {
    return apiRequest(`/chats/${id}/pin`, {
      method: 'POST',
    });
  },

  async deleteChat(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/chats/${id}`, {
      method: 'DELETE',
    });
  },

  // Messages
  async getMessages(chatId: string): Promise<PaginatedResponse<Message>> {
    return apiRequest(`/chats/${chatId}/messages`) as Promise<PaginatedResponse<Message>>;
  },

  async sendMessage(
    chatId: string,
    content: string,
    model?: string
  ): Promise<ApiResponse<{ userMessage: Message; assistantMessage: Message; toolCalls?: ToolCall[] }>> {
    return apiRequest(`/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, model }),
    });
  },

  // Streaming message
  streamMessage(
    chatId: string,
    content: string,
    onChunk: (chunk: StreamChunk) => void,
    onError?: (error: Error) => void,
    model?: string,
    skillIds?: string[]
  ): () => void {
    const url = `${API_BASE_URL}/chats/${chatId}/stream`;
    
    const controller = new AbortController();
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({ content, model, skillIds }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 401) {
            handleAuthExpired();
          }

          // Try to extract error details from the response body
          let errorDetail = `Stream request failed with status ${response.status}`;
          try {
            const errorBody = await response.json();
            errorDetail = errorBody.error?.message || errorBody.message || errorDetail;
          } catch {
            // Response body wasn't JSON
          }
          throw new Error(errorDetail);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        const processLines = (lines: string[]) => {
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const chunk: StreamChunk = JSON.parse(line.slice(6));
                onChunk(chunk);
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // Process any remaining data in the buffer
            if (buffer.trim()) {
              const remainingLines = buffer.split('\n');
              processLines(remainingLines);
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          processLines(lines);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Stream error:', error);
          // Send error as a stream chunk so the UI can display it
          onChunk({ type: 'error', error: error.message || 'Connection failed. Please try again.' });
          onError?.(error);
        }
      });

    // Return cancel function
    return () => controller.abort();
  },

  /**
   * Multi-model streaming — sends the same prompt to multiple models in parallel.
   * Each SSE chunk includes a `model` field so the UI can demux responses.
   */
  multiModelStream(
    chatId: string,
    content: string,
    models: string[],
    onChunk: (chunk: MultiModelStreamChunk) => void,
    onError?: (error: Error) => void,
    skillIds?: string[]
  ): () => void {
    const url = `${API_BASE_URL}/chats/${chatId}/multi-stream`;
    const controller = new AbortController();

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({ content, models, skillIds }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorDetail = `Multi-stream request failed with status ${response.status}`;
          try {
            const errorBody = await response.json();
            errorDetail = errorBody.error?.message || errorBody.message || errorDetail;
          } catch { /* not JSON */ }
          throw new Error(errorDetail);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        const processLines = (lines: string[]) => {
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const chunk: MultiModelStreamChunk = JSON.parse(line.slice(6));
                onChunk(chunk);
              } catch (e) { /* skip invalid JSON */ }
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.trim()) {
              processLines(buffer.split('\n'));
            }
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          processLines(lines);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Multi-stream error:', error);
          onChunk({ type: 'error', model: 'system', error: error.message || 'Connection failed.' });
          onError?.(error);
        }
      });

    return () => controller.abort();
  },

  /**
   * Finalize a model response — saves the selected model's output as the official assistant message
   */
  async finalizeModelResponse(
    chatId: string,
    selectedModel: string,
    selectedContent: string
  ): Promise<ApiResponse<{ assistantMessage: Message; selectedModel: string }>> {
    return apiRequest(`/chats/${chatId}/finalize-model`, {
      method: 'POST',
      body: JSON.stringify({ selectedModel, selectedContent }),
    });
  },

  // ============ RAG / Knowledge Base APIs ============

  // Datasources
  async listDatasources(assistantId: string): Promise<PaginatedResponse<Datasource>> {
    return apiRequest(`/assistants/${assistantId}/datasources`) as Promise<PaginatedResponse<Datasource>>;
  },

  async getDatasource(assistantId: string, datasourceId: string): Promise<ApiResponse<Datasource>> {
    return apiRequest(`/assistants/${assistantId}/datasources/${datasourceId}`);
  },

  async createDatasource(
    assistantId: string,
    data: { name: string; description?: string; type: string; config?: Record<string, unknown> }
  ): Promise<ApiResponse<Datasource>> {
    return apiRequest(`/assistants/${assistantId}/datasources`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteDatasource(assistantId: string, datasourceId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/assistants/${assistantId}/datasources/${datasourceId}`, {
      method: 'DELETE',
    });
  },

  // Document Upload & Ingestion
  async uploadDocument(
    assistantId: string,
    datasourceId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<IngestionResult>> {
    const url = `${API_BASE_URL}/assistants/${assistantId}/datasources/${datasourceId}/upload`;
    
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / e.total) * 100);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error('Invalid response'));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
      xhr.send(formData);
    });
  },

  // RAG Search
  async searchKnowledgeBase(
    assistantId: string,
    query: string,
    options?: {
      datasourceIds?: string[];
      topK?: number;
      minScore?: number;
    }
  ): Promise<ApiResponse<RAGSearchResult>> {
    return apiRequest(`/assistants/${assistantId}/knowledge/search`, {
      method: 'POST',
      body: JSON.stringify({ query, ...options }),
    });
  },

  // Knowledge Base Stats
  async getKnowledgeBaseStats(assistantId: string): Promise<ApiResponse<KnowledgeBaseStats>> {
    return apiRequest(`/assistants/${assistantId}/knowledge/stats`);
  },

  // Get Supported File Types
  async getSupportedFileTypes(): Promise<ApiResponse<string[]>> {
    return apiRequest('/knowledge/supported-types');
  },

  // ============ Knowledge Hub: Questions & Audit History ============

  async listKnowledgeQuestions(datasourceId: string): Promise<ApiResponse<KnowledgeQuestion[]>> {
    return apiRequest(`/datasources/${datasourceId}/questions`);
  },

  async listKnowledgeAudit(
    datasourceId: string,
    questionId?: string
  ): Promise<ApiResponse<KnowledgeAuditEvent[]>> {
    const qs = questionId ? `?questionId=${encodeURIComponent(questionId)}` : '';
    return apiRequest(`/datasources/${datasourceId}/audit${qs}`);
  },

  async generateKnowledgeQuestions(
    datasourceId: string,
    options?: { count?: number; documentName?: string; instructions?: string; stakeholders?: string[] }
  ): Promise<ApiResponse<{ batchId: string; questions: KnowledgeQuestion[] }>> {
    return apiRequest(`/datasources/${datasourceId}/questions/generate`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  },

  async exportKnowledgeQuestions(datasourceId: string, format: 'csv' | 'json' = 'csv'): Promise<void> {
    const url = `${API_BASE_URL}/datasources/${datasourceId}/questions/export?format=${format}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `knowledge-questions.${format}`;
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  },

  async importKnowledgeAnswers(
    datasourceId: string,
    file: File
  ): Promise<ApiResponse<{ updated: number; skipped: number; total: number }>> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE_URL}/datasources/${datasourceId}/questions/import-answers`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
      body: formData,
    });
    return res.json();
  },

  async scoreKnowledgeDocument(
    datasourceId: string
  ): Promise<ApiResponse<{ overallScore: number; answeredCount: number; totalCount: number; scores: { questionId: string; score: number; rationale: string }[]; summary: string }>> {
    return apiRequest(`/datasources/${datasourceId}/questions/score`, { method: 'POST' });
  },

  async addKnowledgeToRag(
    datasourceId: string
  ): Promise<ApiResponse<{ ingested: number; chunks: number; documentId: string }>> {
    return apiRequest(`/datasources/${datasourceId}/questions/add-to-rag`, { method: 'POST' });
  },

  async recordKnowledgeDocumentAdded(
    datasourceId: string,
    documentName: string
  ): Promise<ApiResponse<KnowledgeAuditEvent>> {
    return apiRequest(`/datasources/${datasourceId}/document-added`, {
      method: 'POST',
      body: JSON.stringify({ documentName }),
    });
  },

  async createKnowledgeQuestion(
    datasourceId: string,
    text: string
  ): Promise<ApiResponse<KnowledgeQuestion>> {
    return apiRequest(`/datasources/${datasourceId}/questions`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async updateKnowledgeQuestion(
    datasourceId: string,
    questionId: string,
    text: string
  ): Promise<ApiResponse<KnowledgeQuestion>> {
    return apiRequest(`/datasources/${datasourceId}/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    });
  },

  async deleteKnowledgeQuestion(
    datasourceId: string,
    questionId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/datasources/${datasourceId}/questions/${questionId}`, {
      method: 'DELETE',
    });
  },

  async answerKnowledgeQuestion(
    datasourceId: string,
    questionId: string,
    answer: string
  ): Promise<ApiResponse<KnowledgeQuestion>> {
    return apiRequest(`/datasources/${datasourceId}/questions/${questionId}/answer`, {
      method: 'POST',
      body: JSON.stringify({ answer }),
    });
  },

  async reviewKnowledgeQuestion(
    datasourceId: string,
    questionId: string,
    decision: ReviewDecision,
    notes?: string
  ): Promise<ApiResponse<KnowledgeQuestion>> {
    return apiRequest(`/datasources/${datasourceId}/questions/${questionId}/review`, {
      method: 'POST',
      body: JSON.stringify({ decision, notes }),
    });
  },

  // ============================================
  // MCP Integration APIs
  // ============================================

  // Get all MCP servers
  async getMCPServers(): Promise<ApiResponse<MCPServer[]>> {
    return apiRequest('/mcp/servers');
  },

  // Get tools for a specific server
  async getMCPServerTools(serverName: string): Promise<ApiResponse<MCPTool[]>> {
    return apiRequest(`/mcp/servers/${serverName}/tools`);
  },

  // Get all MCP tools
  async getAllMCPTools(): Promise<ApiResponse<{ tools: MCPTool[]; count: number }>> {
    return apiRequest('/mcp/tools');
  },

  // Execute an MCP tool
  async executeMCPTool(
    serverName: string,
    toolName: string,
    args: Record<string, unknown>
  ): Promise<ApiResponse<MCPToolResult>> {
    return apiRequest('/mcp/execute', {
      method: 'POST',
      body: JSON.stringify({
        serverName,
        toolName,
        arguments: args,
      }),
    });
  },

  // Get OAuth status for a provider
  async getMCPOAuthStatus(provider: string): Promise<ApiResponse<MCPOAuthStatus>> {
    return apiRequest(`/mcp/oauth/${provider}/status`);
  },

  // Get OAuth status for all providers in a single optimized call
  async getAllMCPOAuthStatuses(): Promise<ApiResponse<Record<string, MCPOAuthStatus>>> {
    return apiRequest('/mcp/oauth/status/all');
  },

  // Validate OAuth connection with external API call (slower, use sparingly)
  async validateMCPOAuthStatus(provider: string): Promise<ApiResponse<MCPOAuthStatus>> {
    return apiRequest(`/mcp/oauth/${provider}/status?validate=true`);
  },

  // Disconnect from a provider
  async disconnectMCPProvider(provider: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/mcp/oauth/${provider}/disconnect`, {
      method: 'POST',
    });
  },

  // Get all integrations for the current user
  async getIntegrations(): Promise<ApiResponse<IntegrationSummary[]>> {
    return apiRequest('/mcp/integrations');
  },

  // Get a specific integration for the current user
  async getIntegration(provider: string): Promise<ApiResponse<IntegrationConnection | null>> {
    return apiRequest(`/mcp/integrations/${provider}`);
  },

  // Disconnect an integration
  async disconnectIntegration(provider: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/mcp/integrations/${provider}`, {
      method: 'DELETE',
    });
  },

  // Trigger sync for an integration
  async triggerIntegrationSync(
    provider: string,
    assistantId: string,
    options?: { type?: 'full' | 'incremental'; projectKeys?: string[] }
  ): Promise<ApiResponse<{ jobId: string; status: string; message: string }>> {
    return apiRequest('/mcp/sync/trigger', {
      method: 'POST',
      body: JSON.stringify({ provider, assistantId, ...options }),
    });
  },

  // Get sync status
  async getSyncStatus(): Promise<ApiResponse<{ jobs: any[]; stats: any }>> {
    return apiRequest('/mcp/sync/status');
  },

  // Authenticate with Azure DevOps using PAT
  async authenticateAzureDevOps(
    organization: string,
    personalAccessToken: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest('/mcp/auth/azure-devops', {
      method: 'POST',
      body: JSON.stringify({ organization, personalAccessToken }),
    });
  },

  // Authenticate with GitHub using a Personal Access Token
  async authenticateGitHub(
    personalAccessToken: string
  ): Promise<ApiResponse<{ message: string }>> {
    return apiRequest('/mcp/auth/github', {
      method: 'POST',
      body: JSON.stringify({ personalAccessToken }),
    });
  },

  // Get OAuth authorization URL (returns redirect URL)
  // Includes auth token as query param since browser redirects can't send headers
  getMCPOAuthUrl(provider: string): string {
    const token = getAuthToken();
    return `${API_BASE_URL}/mcp/oauth/${provider}/authorize?token=${encodeURIComponent(token)}`;
  },

  // ============================================
  // Global Datasources APIs (not tied to assistant)
  // ============================================

  // List all global datasources for the user
  async listAllDatasources(): Promise<ApiResponse<Datasource[]>> {
    return apiRequest('/datasources');
  },

  // Create a global datasource
  async createGlobalDatasource(data: {
    name: string;
    description?: string;
    type: 'file' | 'url';
  }): Promise<ApiResponse<Datasource>> {
    return apiRequest('/datasources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Upload file to a global datasource
  async uploadToDatasource(
    datasourceId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<IngestionResult>> {
    const url = `${API_BASE_URL}/datasources/${datasourceId}/upload`;
    
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / e.total) * 100);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error('Invalid response'));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
      xhr.send(formData);
    });
  },

  // Delete a global datasource
  async deleteGlobalDatasource(datasourceId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/datasources/${datasourceId}`, {
      method: 'DELETE',
    });
  },

  // ============================================
  // Embedding / RAG Model Settings APIs
  // ============================================

  // Get current embedding configuration and available models
  async getEmbeddingConfig(): Promise<ApiResponse<EmbeddingConfig>> {
    return apiRequest('/settings/embedding');
  },

  // Set embedding use-case preset (budget, enterprise, code, multilingual)
  async setEmbeddingUseCase(useCase: string): Promise<ApiResponse<{ message: string; config: any }>> {
    return apiRequest('/settings/embedding/use-case', {
      method: 'POST',
      body: JSON.stringify({ useCase }),
    });
  },

  // Set custom embedding provider/model
  async setEmbeddingProvider(provider: string, model: string, dimensions?: number): Promise<ApiResponse<{ message: string; config: any }>> {
    return apiRequest('/settings/embedding/provider', {
      method: 'POST',
      body: JSON.stringify({ provider, model, dimensions }),
    });
  },

  // Get AI-recommended embedding model based on description
  async getEmbeddingRecommendation(description: string): Promise<ApiResponse<{ recommendation: any; modelInfo: any }>> {
    return apiRequest('/settings/embedding/recommend', {
      method: 'POST',
      body: JSON.stringify({ description }),
    });
  },

  // Test current embedding configuration
  async testEmbedding(text?: string): Promise<ApiResponse<{ dimensions: number; processingTime: string; provider: any }>> {
    return apiRequest('/settings/embedding/test', {
      method: 'POST',
      body: JSON.stringify({ text: text || 'Hello, world!' }),
    });
  },

  // Get all connected integrations
  async getConnectedIntegrations(): Promise<ApiResponse<IntegrationSummary[]>> {
    return apiRequest('/mcp/integrations');
  },

  // ============================================
  // Custom Integrations APIs
  // ============================================

  // Test custom integration connection
  async testCustomIntegration(data: {
    authType: string;
    baseUrl: string;
    credentials: Record<string, unknown>;
  }): Promise<ApiResponse<{ success: boolean; message: string; latencyMs?: number }>> {
    return apiRequest('/integrations/custom/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Create a new custom integration
  async createCustomIntegration(data: {
    name: string;
    description?: string;
    authType: string;
    baseUrl: string;
    iconUrl?: string;
    credentials: Record<string, unknown>;
    tools?: CustomToolDefinition[];
  }): Promise<ApiResponse<CustomIntegration>> {
    return apiRequest('/integrations/custom', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get all custom integrations
  async listCustomIntegrations(): Promise<ApiResponse<CustomIntegration[]>> {
    return apiRequest('/integrations/custom');
  },

  // Update a custom integration
  async updateCustomIntegration(
    id: string,
    data: {
      name?: string;
      description?: string;
      authType?: string;
      baseUrl?: string;
      iconUrl?: string;
      credentials?: Record<string, unknown>;
      tools?: CustomToolDefinition[];
    }
  ): Promise<ApiResponse<CustomIntegration>> {
    return apiRequest(`/integrations/custom/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Delete a custom integration
  async deleteCustomIntegration(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/integrations/custom/${id}`, {
      method: 'DELETE',
    });
  },
};

// MCP Types
export interface MCPServer {
  name: string;
  version: string;
  description: string;
  toolCount: number;
}

export interface MCPTool {
  name: string;
  description: string;
  inputSchema: {
    type: string;
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface MCPToolResult {
  success: boolean;
  data?: unknown;
  error?: string;
  metadata?: {
    duration?: number;
    source?: string;
  };
}

export interface MCPOAuthStatus {
  connected: boolean;
  provider: string;
  expiresAt?: string;
  accountInfo?: {
    cloudId?: string;
    siteUrl?: string;
    siteName?: string;
    email?: string;
    name?: string;
    avatarUrl?: string;
    organization?: string;
  };
}

// Integration types
export interface IntegrationConnection {
  id: string;
  userId: string;
  organizationId?: string;
  provider: 'jira' | 'confluence' | 'azure-devops' | 'github' | 'slack';
  status: 'connected' | 'disconnected' | 'expired' | 'error';
  displayName: string;
  accountInfo?: {
    cloudId?: string;
    siteUrl?: string;
    siteName?: string;
    email?: string;
    name?: string;
    avatarUrl?: string;
    organization?: string;
  };
  scopes?: string[];
  connectedAt?: string;
  lastUsedAt?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationSummary {
  provider: string;
  connected: boolean;
  displayName?: string;
  accountInfo?: IntegrationConnection['accountInfo'];
  connectedAt?: string;
  lastUsedAt?: string;
}

// Custom Integration types
export type AuthType = 'oauth2' | 'api-key' | 'basic-auth';
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface OAuthCredentials {
  clientId: string;
  clientSecret: string;
  authorizationUrl: string;
  tokenUrl: string;
  scopes: string;
}

export interface ApiKeyCredentials {
  apiKey: string;
  headerName?: string;
}

export interface BasicAuthCredentials {
  username: string;
  password: string;
}

export interface CustomToolDefinition {
  name: string;
  description: string;
  method: HttpMethod;
  path: string;
  headers?: Record<string, string>;
  inputSchema: {
    type: 'object';
    properties: Record<string, {
      type: string;
      description?: string;
      enum?: string[];
      default?: unknown;
    }>;
    required?: string[];
  };
  responseMapping?: {
    dataPath?: string;
  };
}

export interface CustomIntegration {
  id: string;
  name: string;
  description?: string;
  authType: AuthType;
  baseUrl: string;
  iconUrl?: string;
  status: 'connected' | 'disconnected' | 'error';
  tools: CustomToolDefinition[];
  toolCount: number;
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Template Types
// ============================================================================

export interface AgentCapability {
  id: string;
  name: string;
  description: string;
  icon?: string;
  category?: 'core' | 'enhancement' | 'analysis' | 'integration';
  priority?: 'P0' | 'P1' | 'P2' | 'P3';
  enabled?: boolean;
  features?: string[];
  tools?: string[];
}

export interface AgentIntegration {
  id: string;
  name: string;
  icon?: string;
  type: string;
  permissions?: string[];
  description: string;
  useCases?: string[];
  required?: boolean;
}

export interface AgentTemplate {
  id: string;
  name: string;
  shortName?: string;
  shortDescription?: string;
  description: string;
  longDescription?: string;
  version: string;
  author: string;
  authorId?: string;
  category: string;
  subcategory?: string;
  tags: string[];
  icon: string;
  avatar?: string;
  color?: string;
  llmModel?: string;
  temperature?: number;
  maxTokens?: number;
  systemInstructions: string;
  conversationStarters: string[];
  capabilities: AgentCapability[];
  integrations: AgentIntegration[];
  requiredIntegrations?: string[];
  optionalIntegrations?: string[];
  enabledTools?: string[];
  isPublic?: boolean;
  isVerified?: boolean;
  isFeatured?: boolean;
  verified?: boolean;
  featured?: boolean;
  pricing?: 'free' | 'premium' | 'enterprise';
  // Support both field names from API
  cloneCount?: number;
  totalClones?: number;
  totalUses?: number;
  rating?: number;
  averageRating?: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface TemplateCategory {
  id: string;
  name: string;
  count: number;
}

export interface TemplateTag {
  tag: string;
  count: number;
}

export interface CloneTemplateRequest {
  customName?: string;
  customDescription?: string;
  customSystemInstructions?: string;
  customConversationStarters?: string[];
  customTemperature?: number;
  customMaxTokens?: number;
  customLlmModel?: string;
}

export interface CloneTemplateResponse {
  assistant: Assistant;
  template: {
    id: string;
    name: string;
  };
  customizations: {
    name: boolean;
    description: boolean;
    systemInstructions: boolean;
    conversationStarters: boolean;
    temperature: boolean;
    maxTokens: boolean;
    llmModel: boolean;
  };
}

export interface UserTemplate {
  id: string;
  userId: string;
  organizationId?: string;
  name: string;
  description: string;
  shortDescription?: string;
  category: string;
  icon?: string;
  llmModel?: string;
  temperature?: number;
  maxTokens?: number;
  systemInstructions: string;
  conversationStarters: string[];
  enabledTools?: string[];
  capabilities?: AgentCapability[];
  integrations?: AgentIntegration[];
  isPublic?: boolean;
  isShared?: boolean;
  sharedWithTeamIds?: string[];
  basedOnTemplateId?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserTemplateRequest {
  name: string;
  description: string;
  shortDescription?: string;
  category?: string;
  icon?: string;
  llmModel?: string;
  temperature?: number;
  maxTokens?: number;
  systemInstructions: string;
  conversationStarters?: string[];
  enabledTools?: string[];
  capabilities?: AgentCapability[];
  integrations?: AgentIntegration[];
  tags?: string[];
  basedOnTemplateId?: string;
}

export interface TemplateReview {
  id: string;
  templateId: string;
  userId: string;
  userName: string;
  rating: number;
  title: string;
  comment: string;
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Template API Methods
// ============================================================================

const templateApi = {
  // Marketplace templates
  getMarketplaceTemplates: async (params?: {
    search?: string;
    category?: string;
    tags?: string[];
    pricing?: string[];
    isVerified?: boolean;
    isFeatured?: boolean;
    sortBy?: 'popular' | 'rating' | 'newest' | 'name';
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<AgentTemplate>> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.category) queryParams.set('category', params.category);
    if (params?.tags?.length) queryParams.set('tags', params.tags.join(','));
    if (params?.pricing?.length) queryParams.set('pricing', params.pricing.join(','));
    if (params?.isVerified !== undefined) queryParams.set('isVerified', String(params.isVerified));
    if (params?.isFeatured !== undefined) queryParams.set('isFeatured', String(params.isFeatured));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    
    const response = await apiRequest<AgentTemplate[]>(`/templates?${queryParams}`);
    return response as unknown as PaginatedResponse<AgentTemplate>;
  },

  getTemplateById: async (id: string): Promise<ApiResponse<AgentTemplate>> => {
    return apiRequest<AgentTemplate>(`/templates/${id}`);
  },

  getFeaturedTemplates: async (): Promise<ApiResponse<AgentTemplate[]>> => {
    return apiRequest<AgentTemplate[]>('/templates/featured');
  },

  getTemplatesByCategory: async (category: string): Promise<ApiResponse<AgentTemplate[]>> => {
    return apiRequest<AgentTemplate[]>(`/templates/category/${category}`);
  },

  getCategories: async (): Promise<ApiResponse<TemplateCategory[]>> => {
    return apiRequest<TemplateCategory[]>('/templates/categories');
  },

  getPopularTags: async (limit?: number): Promise<ApiResponse<TemplateTag[]>> => {
    const params = limit ? `?limit=${limit}` : '';
    return apiRequest<TemplateTag[]>(`/templates/tags${params}`);
  },

  cloneTemplate: async (templateId: string, request?: CloneTemplateRequest): Promise<ApiResponse<CloneTemplateResponse>> => {
    return apiRequest<CloneTemplateResponse>(`/templates/${templateId}/clone`, {
      method: 'POST',
      body: JSON.stringify(request || {}),
    });
  },

  // User templates
  getUserTemplates: async (): Promise<ApiResponse<UserTemplate[]>> => {
    return apiRequest<UserTemplate[]>('/templates/user');
  },

  getUserTemplateById: async (id: string): Promise<ApiResponse<UserTemplate>> => {
    return apiRequest<UserTemplate>(`/templates/user/${id}`);
  },

  createUserTemplate: async (request: CreateUserTemplateRequest): Promise<ApiResponse<UserTemplate>> => {
    return apiRequest<UserTemplate>('/templates/user', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  updateUserTemplate: async (id: string, updates: Partial<CreateUserTemplateRequest>): Promise<ApiResponse<UserTemplate>> => {
    return apiRequest<UserTemplate>(`/templates/user/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  deleteUserTemplate: async (id: string): Promise<ApiResponse<{ deleted: boolean }>> => {
    return apiRequest<{ deleted: boolean }>(`/templates/user/${id}`, {
      method: 'DELETE',
    });
  },

  createAssistantFromUserTemplate: async (templateId: string, customizations?: Partial<CreateUserTemplateRequest>): Promise<ApiResponse<Assistant>> => {
    return apiRequest<Assistant>(`/templates/user/${templateId}/create-assistant`, {
      method: 'POST',
      body: JSON.stringify(customizations || {}),
    });
  },

  // Reviews
  getTemplateReviews: async (templateId: string, page?: number, limit?: number): Promise<PaginatedResponse<TemplateReview>> => {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    const response = await apiRequest<TemplateReview[]>(`/templates/${templateId}/reviews?${params}`);
    return response as unknown as PaginatedResponse<TemplateReview>;
  },

  createReview: async (templateId: string, review: { rating: number; title: string; comment: string }): Promise<ApiResponse<TemplateReview>> => {
    return apiRequest<TemplateReview>(`/templates/${templateId}/reviews`, {
      method: 'POST',
      body: JSON.stringify(review),
    });
  },
};

// ============================================================================
// Analytics Types
// ============================================================================

export type AnalyticsTimePeriod = 
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_60_days'
  | 'last_90_days'
  | 'this_month'
  | 'last_month'
  | 'custom';

export interface AnalyticsFilters {
  timePeriod?: AnalyticsTimePeriod;
  startDate?: string;
  endDate?: string;
  userIds?: string[];
  projectIds?: string[];
  meOnly?: boolean;
}

export interface SummaryMetrics {
  llmInputTokens: number;
  llmOutputTokens: number;
  llmCachedInputTokens: number;
  totalMoneySpent: number;
  llmCost: number;
  cliCost: number;
  embeddingInputTokens: number;
  embeddingCost: number;
  uniqueActiveUsers: number;
  uniqueAssistantsInvoked: number;
  uniqueWorkflowsInvoked: number;
}

export interface TopAssistantStats {
  assistantId: string;
  assistantName: string;
  totalChats: number;
  totalMessages: number;
}

export interface WorkflowPerformanceStats {
  workflowId: string;
  workflowName: string;
  totalRuns: number;
  successCount: number;
  failedCount: number;
  avgDurationMs: number;
}

export interface LLMModelDistribution {
  model: string;
  totalRequests: number;
  percentage: number;
}

export interface EmbeddingModelDistribution {
  model: string;
  totalInvocations: number;
  percentage: number;
}

export interface ToolUsageStats {
  toolName: string;
  toolType: string;
  totalInvocations: number;
  uniqueUsers: number;
  successRate: number;
  avgLatencyMs: number;
}

export interface AgentUsageStats {
  assistantId: string;
  assistantName: string;
  totalConversations: number;
  totalMessages: number;
}

export interface WebhookInvocationStats {
  userId: string;
  userName?: string;
  webhookAlias: string;
  totalInvocations: number;
}

export interface UserSpendingStats {
  userId: string;
  userName?: string;
  totalSpent: number;
  percentage: number;
}

export interface ProjectSpendingStats {
  projectId: string;
  projectName?: string;
  totalSpent: number;
  percentage: number;
}

export interface MCPServerUsageStats {
  serverName: string;
  serverType: string;
  totalRequests: number;
  percentage: number;
}

export interface MCPServerByUserStats {
  userId: string;
  userName?: string;
  serverName: string;
  totalRequests: number;
}

export interface CLISummaryMetrics {
  uniqueUsers: number;
  uniqueSessions: number;
  uniqueRepositories: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokensRead: number;
  totalCost: number;
  cacheReadCost: number;
  cacheCreationCost: number;
  cliInvoked: number;
  avgSessionDurationMs: number | null;
  maxSessionDurationMs: number | null;
  proxySuccessRate: number | null;
  proxyFailedCalls: number;
  netNewLines: number;
  totalCreatedFiles: number;
  totalDeletedLines: number;
  totalDeletedFiles: number;
  totalModifiedFiles: number;
  totalPrompts: number;
}

export interface AnalyticsDashboardData {
  filters: AnalyticsFilters;
  summary: SummaryMetrics;
  topAssistants: TopAssistantStats[];
  workflowPerformance: WorkflowPerformanceStats[];
  llmModelDistribution: LLMModelDistribution[];
  embeddingModelDistribution: EmbeddingModelDistribution[];
  toolUsage: ToolUsageStats[];
  agentUsage: AgentUsageStats[];
  webhookInvocations: WebhookInvocationStats[];
  userSpending: UserSpendingStats[];
  projectSpending: ProjectSpendingStats[];
  mcpServerUsage: MCPServerUsageStats[];
  mcpServersByUser: MCPServerByUserStats[];
  cliMetrics: CLISummaryMetrics;
  modelTokenUsage: ModelTokenUsage[];
}

/**
 * Per-Model Token Usage
 */
export interface ModelTokenUsage {
  model: string;
  provider: string;
  totalRequests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  totalCost: number;
  avgCostPerRequest: number;
  userKeyRequests: number;
  serverKeyRequests: number;
  userKeyCost: number;
  serverKeyCost: number;
}

// ============================================================================
// Analytics API
// ============================================================================

export const analyticsApi = {
  /**
   * Get full analytics dashboard data
   */
  getDashboard: async (filters?: AnalyticsFilters): Promise<ApiResponse<AnalyticsDashboardData>> => {
    const params = new URLSearchParams();
    if (filters?.timePeriod) params.set('timePeriod', filters.timePeriod);
    if (filters?.startDate) params.set('startDate', filters.startDate);
    if (filters?.endDate) params.set('endDate', filters.endDate);
    if (filters?.userIds?.length) params.set('userIds', filters.userIds.join(','));
    if (filters?.projectIds?.length) params.set('projectIds', filters.projectIds.join(','));
    if (filters?.meOnly) params.set('meOnly', 'true');
    
    const queryString = params.toString();
    return apiRequest<AnalyticsDashboardData>(`/analytics/dashboard${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get summary metrics only
   */
  getSummary: async (filters?: AnalyticsFilters): Promise<ApiResponse<SummaryMetrics>> => {
    const params = new URLSearchParams();
    if (filters?.timePeriod) params.set('timePeriod', filters.timePeriod);
    if (filters?.meOnly) params.set('meOnly', 'true');
    
    const queryString = params.toString();
    return apiRequest<SummaryMetrics>(`/analytics/summary${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Track a generic event
   */
  trackEvent: async (type: string, metadata: Record<string, any>): Promise<ApiResponse<void>> => {
    return apiRequest<void>('/analytics/track', {
      method: 'POST',
      body: JSON.stringify({ type, metadata }),
    });
  },

  /**
   * Track chat message
   */
  trackChatMessage: async (data: {
    assistantId: string;
    assistantName: string;
    chatId: string;
    model: string;
    provider: string;
    inputTokens: number;
    outputTokens: number;
    cost: number;
  }): Promise<ApiResponse<void>> => {
    return apiRequest<void>('/analytics/track/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Track LLM usage
   */
  trackLLMUsage: async (data: {
    model: string;
    provider: string;
    inputTokens: number;
    outputTokens: number;
    cost: number;
    assistantId?: string;
    chatId?: string;
  }): Promise<ApiResponse<void>> => {
    return apiRequest<void>('/analytics/track/llm', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// Workflow API Types
// ============================================================================

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  iconUrl?: string;
  author: string;
  categories: string[];
  tags: string[];
  isSharedWithProject: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowMetaState {
  id: string;
  type: string;
  isConnected: boolean;
  position: { x: number; y: number };
  measured?: { width: number; height: number };
  selected?: boolean;
  data?: Record<string, any>;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type?: string;
  animated?: boolean;
  label?: string;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  iconUrl?: string;
  organizationId: string;
  projectId?: string;
  authorId: string;
  authorName?: string;
  isSharedWithProject: boolean;
  isTemplate: boolean;
  templateId?: string;
  metaStates?: WorkflowMetaState[];
  edges?: WorkflowEdge[];
  states?: any[];
  config?: Record<string, any>;
  assistants?: any[];
  status: 'draft' | 'active' | 'archived' | 'template';
  version: string;
  runCount: number;
  successCount: number;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
  lastRunAt?: string;
}

export interface WorkflowListResponse {
  items: Workflow[];
  total: number;
  hasMore: boolean;
}

export interface TemplateListResponse {
  templates: WorkflowTemplate[];
  total: number;
}

// ============================================================================
// Workflow API
// ============================================================================

export const workflowApi = {
  /**
   * Get all workflow templates
   */
  getTemplates: async (filters?: { categories?: string[]; search?: string }): Promise<ApiResponse<TemplateListResponse>> => {
    const params = new URLSearchParams();
    if (filters?.categories?.length) params.set('categories', filters.categories.join(','));
    if (filters?.search) params.set('search', filters.search);
    
    const queryString = params.toString();
    return apiRequest<TemplateListResponse>(`/workflows/templates${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get template by ID
   */
  getTemplateById: async (id: string): Promise<ApiResponse<WorkflowTemplate>> => {
    return apiRequest<WorkflowTemplate>(`/workflows/templates/${id}`);
  },

  /**
   * Get template count
   */
  getTemplateCount: async (): Promise<ApiResponse<{ count: number }>> => {
    return apiRequest<{ count: number }>('/workflows/templates/count');
  },

  /**
   * Get all workflows
   */
  getWorkflows: async (filters?: {
    status?: string;
    projectId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<WorkflowListResponse>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.projectId) params.set('projectId', filters.projectId);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    
    const queryString = params.toString();
    return apiRequest<WorkflowListResponse>(`/workflows${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get my workflows
   */
  getMyWorkflows: async (page?: number, limit?: number): Promise<ApiResponse<WorkflowListResponse>> => {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    
    const queryString = params.toString();
    return apiRequest<WorkflowListResponse>(`/workflows/my${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get workflow by ID
   */
  getWorkflowById: async (id: string): Promise<ApiResponse<Workflow>> => {
    return apiRequest<Workflow>(`/workflows/${id}`);
  },

  /**
   * Create a new workflow
   */
  createWorkflow: async (data: {
    name: string;
    description: string;
    templateId?: string;
    projectId?: string;
    isSharedWithProject?: boolean;
  }): Promise<ApiResponse<Workflow>> => {
    return apiRequest<Workflow>('/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update workflow
   */
  updateWorkflow: async (id: string, updates: Partial<Workflow>): Promise<ApiResponse<Workflow>> => {
    return apiRequest<Workflow>(`/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete workflow
   */
  deleteWorkflow: async (id: string): Promise<ApiResponse<{ deleted: boolean }>> => {
    return apiRequest<{ deleted: boolean }>(`/workflows/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get workflow YAML
   */
  getWorkflowYAML: async (id: string): Promise<ApiResponse<{ yaml: string }>> => {
    return apiRequest<{ yaml: string }>(`/workflows/${id}/yaml`);
  },

  /**
   * Import YAML into workflow
   */
  importWorkflowYAML: async (id: string, yaml: string): Promise<ApiResponse<Workflow>> => {
    return apiRequest<Workflow>(`/workflows/${id}/yaml`, {
      method: 'POST',
      body: JSON.stringify({ yaml }),
    });
  },

  /**
   * Run a workflow (simple mode - creates run record)
   */
  runWorkflow: async (id: string, data?: { variables?: Record<string, any>; context?: Record<string, any> }): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/workflows/${id}/run`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },

  /**
   * Execute a workflow (full agentic execution - stage by stage)
   * This triggers the workflow execution engine that processes states sequentially
   */
  executeWorkflow: async (id: string, data?: { 
    variables?: Record<string, any>; 
    context?: Record<string, any>;
    startFromState?: string;
  }): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/workflows/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  },

  /**
   * Get workflow execution status with detailed progress
   */
  getExecutionStatus: async (workflowId: string, runId: string): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/workflows/${workflowId}/execute/${runId}/status`);
  },

  /**
   * Resume a paused workflow execution
   */
  resumeExecution: async (workflowId: string, runId: string, data: {
    userInput?: string;
    approvalDecision?: 'approved' | 'rejected';
  }): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/workflows/${workflowId}/execute/${runId}/resume`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * List workflow runs
   */
  listRuns: async (workflowId: string, page?: number, limit?: number): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    
    const queryString = params.toString();
    return apiRequest<any>(`/workflows/${workflowId}/runs${queryString ? `?${queryString}` : ''}`);
  },
};

// ============================================================================
// Skills API Methods
// ============================================================================

const skillsApi = {
  /**
   * List all skills with optional filters
   */
  list: async (params?: {
    search?: string;
    category?: string;
    visibility?: string;
    project?: string;
    createdByMe?: boolean;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ skills: Skill[]; total: number }>> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.category) queryParams.set('category', params.category);
    if (params?.visibility) queryParams.set('visibility', params.visibility);
    if (params?.project) queryParams.set('project', params.project);
    if (params?.createdByMe !== undefined) queryParams.set('createdByMe', String(params.createdByMe));
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    
    const queryString = queryParams.toString();
    return apiRequest<{ skills: Skill[]; total: number }>(`/skills${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single skill by ID
   */
  getById: async (id: string): Promise<ApiResponse<Skill>> => {
    return apiRequest<Skill>(`/skills/${id}`);
  },

  /**
   * Create a new skill
   */
  create: async (data: CreateSkillRequest): Promise<ApiResponse<Skill>> => {
    return apiRequest<Skill>('/skills', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing skill
   */
  update: async (id: string, data: UpdateSkillRequest): Promise<ApiResponse<Skill>> => {
    return apiRequest<Skill>(`/skills/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a skill
   */
  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiRequest<void>(`/skills/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Duplicate a skill
   */
  duplicate: async (id: string): Promise<ApiResponse<Skill>> => {
    return apiRequest<Skill>(`/skills/${id}/duplicate`, {
      method: 'POST',
    });
  },

  /**
   * Export a skill as a file
   */
  exportAsFile: async (id: string): Promise<ApiResponse<Blob>> => {
    return apiRequest<Blob>(`/skills/${id}/export`);
  },
};

/**
 * Meeting API
 */
const meetingApi = {
  /**
   * Get all meetings with optional filters
   */
  getMeetings: async (filters?: import('../types').MeetingFilters): Promise<ApiResponse<import('../types').Meeting[]>> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.organizer) params.append('organizer', filters.organizer);
    if (filters?.botStatus) params.append('botStatus', filters.botStatus);
    
    const queryString = params.toString();
    const url = queryString ? `/meetings?${queryString}` : '/meetings';
    return apiRequest<import('../types').Meeting[]>(url);
  },

  /**
   * Get meeting by ID
   */
  getMeetingById: async (id: string): Promise<ApiResponse<import('../types').Meeting>> => {
    return apiRequest<import('../types').Meeting>(`/meetings/${id}`);
  },

  /**
   * Get meeting transcript
   */
  getMeetingTranscript: async (id: string): Promise<ApiResponse<import('../types').Transcript>> => {
    return apiRequest<import('../types').Transcript>(`/meetings/${id}/transcript`);
  },

  /**
   * Get meeting recording URL
   */
  getMeetingRecording: async (id: string): Promise<ApiResponse<{ url: string }>> => {
    return apiRequest<{ url: string }>(`/meetings/${id}/recording`);
  },

  /**
   * Get meeting minutes (MoM)
   */
  getMeetingMom: async (id: string): Promise<ApiResponse<import('../types').MeetingMinutes>> => {
    return apiRequest<import('../types').MeetingMinutes>(`/meetings/${id}/mom`);
  },

  /**
   * Update meeting minutes
   */
  updateMeetingMom: async (
    id: string, 
    mom: Partial<import('../types').MeetingMinutes>
  ): Promise<ApiResponse<import('../types').MeetingMinutes>> => {
    return apiRequest<import('../types').MeetingMinutes>(`/meetings/${id}/mom`, {
      method: 'PUT',
      body: JSON.stringify(mom),
    });
  },

  /**
   * Approve meeting minutes and create JIRA items
   */
  approveMom: async (id: string): Promise<ApiResponse<import('../types').ApprovalResult>> => {
    return apiRequest<import('../types').ApprovalResult>(`/meetings/${id}/mom/approve`, {
      method: 'POST',
    });
  },

  /**
   * Reject meeting minutes with feedback
   */
  rejectMom: async (id: string, feedback: string): Promise<ApiResponse<import('../types').MeetingMinutes>> => {
    return apiRequest<import('../types').MeetingMinutes>(`/meetings/${id}/mom/reject`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
  },

  /**
   * Send meeting minutes to recipients
   */
  sendMom: async (id: string, recipients: string[]): Promise<ApiResponse<{ success: boolean }>> => {
    return apiRequest<{ success: boolean }>(`/meetings/${id}/mom/send`, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    });
  },

  /**
   * Get proposed JIRA items for a meeting
   */
  getMeetingJiraItems: async (id: string): Promise<ApiResponse<import('../types').JiraItem[]>> => {
    return apiRequest<import('../types').JiraItem[]>(`/meetings/${id}/jira-items`);
  },

  /**
   * Update proposed JIRA items
   */
  updateJiraItems: async (
    id: string, 
    items: import('../types').JiraItem[]
  ): Promise<ApiResponse<import('../types').JiraItem[]>> => {
    return apiRequest<import('../types').JiraItem[]>(`/meetings/${id}/jira-items`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  },

  /**
   * Get meeting settings
   */
  getMeetingSettings: async (): Promise<ApiResponse<import('../types').MeetingSettings>> => {
    return apiRequest<import('../types').MeetingSettings>('/meetings/settings');
  },

  /**
   * Update meeting settings
   */
  updateMeetingSettings: async (
    settings: import('../types').MeetingSettings
  ): Promise<ApiResponse<import('../types').MeetingSettings>> => {
    return apiRequest<import('../types').MeetingSettings>('/meetings/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  /**
   * Get meeting statistics
   */
  getMeetingStats: async (): Promise<ApiResponse<import('../types').MeetingStats>> => {
    return apiRequest<import('../types').MeetingStats>('/meetings/stats');
  },
};

/**
 * Developer Agent API
 * API methods for Developer Agent Dashboard feature
 */
const developerAgentApi = {
  /**
   * Get developer agent statistics
   */
  getStats: async (): Promise<ApiResponse<import('../types').DevAgentStats>> => {
    return apiRequest<import('../types').DevAgentStats>('/developer-agents/stats');
  },

  /**
   * Get list of connected repositories
   */
  getConnectedRepos: async (): Promise<ApiResponse<import('../types').ConnectedRepo[]>> => {
    return apiRequest<import('../types').ConnectedRepo[]>('/developer-agents/repos');
  },

  /**
   * Get user's GitHub organizations
   */
  getGitHubOrgs: async (): Promise<ApiResponse<import('../types').GitHubOrg[]>> => {
    return apiRequest<import('../types').GitHubOrg[]>('/developer-agents/github/orgs');
  },

  /**
   * Get repositories for a GitHub organization
   */
  getGitHubRepos: async (org: string): Promise<ApiResponse<import('../types').GitHubRepo[]>> => {
    return apiRequest<import('../types').GitHubRepo[]>(`/developer-agents/github/orgs/${org}/repos`);
  },

  /**
   * Analyze a repository codebase
   */
  analyzeRepo: async (repoFullName: string, options?: {
    includeRAG?: boolean;
    embeddingModel?: { provider: string; model: string; dimensions: number };
    generateAgents?: boolean;
    generateSkills?: boolean;
    branch?: string;
  }): Promise<ApiResponse<{ analysisId: string }>> => {
    return apiRequest<{ analysisId: string }>('/developer-agents/analyze', {
      method: 'POST',
      body: JSON.stringify({ repoFullName, options }),
    });
  },

  /**
   * Get analysis status
   */
  getAnalysisStatus: async (analysisId: string): Promise<ApiResponse<import('../types').AnalysisStatus>> => {
    return apiRequest<import('../types').AnalysisStatus>(`/developer-agents/analysis/${analysisId}/status`);
  },

  /**
   * Get analysis result
   */
  getAnalysisResult: async (analysisId: string): Promise<ApiResponse<import('../types').CodebaseAnalysis>> => {
    return apiRequest<import('../types').CodebaseAnalysis>(`/developer-agents/analysis/${analysisId}`);
  },

  /**
   * Generate agents and skills from analysis
   */
  generateAgents: async (analysisId: string): Promise<ApiResponse<import('../types').GenerationResult>> => {
    return apiRequest<import('../types').GenerationResult>(`/developer-agents/analysis/${analysisId}/generate`, {
      method: 'POST',
    });
  },

  /**
   * Get generated agents for a repository
   */
  getRepoAgents: async (repoId: string): Promise<ApiResponse<import('../types').GeneratedAgent[]>> => {
    return apiRequest<import('../types').GeneratedAgent[]>(`/developer-agents/repos/${repoId}/agents`);
  },

  /**
   * Get generated skills for a repository
   */
  getRepoSkills: async (repoId: string): Promise<ApiResponse<import('../types').GeneratedSkill[]>> => {
    return apiRequest<import('../types').GeneratedSkill[]>(`/developer-agents/repos/${repoId}/skills`);
  },

  /**
   * Update a generated agent
   */
  updateAgent: async (
    agentId: string,
    data: Partial<import('../types').GeneratedAgent>
  ): Promise<ApiResponse<import('../types').GeneratedAgent>> => {
    return apiRequest<import('../types').GeneratedAgent>(`/developer-agents/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a generated skill
   */
  updateSkill: async (
    skillId: string,
    data: Partial<import('../types').GeneratedSkill>
  ): Promise<ApiResponse<import('../types').GeneratedSkill>> => {
    return apiRequest<import('../types').GeneratedSkill>(`/developer-agents/skills/${skillId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a generated agent
   */
  deleteAgent: async (agentId: string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest<{ message: string }>(`/developer-agents/agents/${agentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete a generated skill
   */
  deleteSkill: async (skillId: string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest<{ message: string }>(`/developer-agents/skills/${skillId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Push agents and skills to repository as PR
   */
  pushToRepo: async (
    analysisId: string,
    options: import('../types').PushOptions
  ): Promise<ApiResponse<import('../types').PushResult>> => {
    return apiRequest<import('../types').PushResult>(`/developer-agents/analysis/${analysisId}/push`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },

  /**
   * Get push status
   */
  getPushStatus: async (analysisId: string): Promise<ApiResponse<import('../types').PushStatus>> => {
    return apiRequest<import('../types').PushStatus>(`/developer-agents/analysis/${analysisId}/push/status`);
  },

  /**
   * Get file previews for push
   */
  getPushFilePreview: async (analysisId: string): Promise<ApiResponse<import('../types').PushFilePreview[]>> => {
    return apiRequest<import('../types').PushFilePreview[]>(`/developer-agents/analysis/${analysisId}/push/preview`);
  },

  /**
   * Sync repository (re-analyze)
   */
  syncRepo: async (repoId: string): Promise<ApiResponse<{ analysisId: string }>> => {
    return apiRequest<{ analysisId: string }>(`/developer-agents/repos/${repoId}/sync`, {
      method: 'POST',
    });
  },

  /**
   * Send blind prompt to playground
   */
  sendBlindPrompt: async (
    repoId: string,
    prompt: string,
    agentId?: string
  ): Promise<ApiResponse<import('../types').BlindPromptResult>> => {
    return apiRequest<import('../types').BlindPromptResult>(`/developer-agents/repos/${repoId}/playground`, {
      method: 'POST',
      body: JSON.stringify({ prompt, agentId }),
    });
  },

  /**
   * Stream blind prompt response (SSE)
   */
  streamBlindPrompt: (repoId: string, prompt: string, agentId?: string): EventSource => {
    const url = new URL(`${API_BASE_URL}/developer-agents/repos/${repoId}/playground/stream`);
    url.searchParams.append('prompt', prompt);
    if (agentId) {
      url.searchParams.append('agentId', agentId);
    }
    return new EventSource(url.toString());
  },

  /**
   * Subscribe to analysis progress (WebSocket/SSE)
   */
  subscribeToAnalysisProgress: (analysisId: string): EventSource => {
    const url = `${API_BASE_URL}/developer-agents/analysis/${analysisId}/progress`;
    return new EventSource(url);
  },

  /**
   * Subscribe to push progress (WebSocket/SSE)
   */
  subscribeToPushProgress: (analysisId: string): EventSource => {
    const url = `${API_BASE_URL}/developer-agents/analysis/${analysisId}/push/progress`;
    return new EventSource(url);
  },
};

// ============================================================================
// Brownfield Analysis API
// ============================================================================

const brownfieldApi = {
  /** List all brownfield projects */
  getProjects: (): Promise<ApiResponse<import('../types/brownfield.types').BrownfieldProjectSummary[]>> =>
    apiRequest<import('../types/brownfield.types').BrownfieldProjectSummary[]>('/brownfield/projects'),

  /** Get full project details */
  getProject: (id: string): Promise<ApiResponse<import('../types/brownfield.types').BrownfieldProject>> =>
    apiRequest<import('../types/brownfield.types').BrownfieldProject>(`/brownfield/projects/${id}`),

  /** Create a new brownfield project */
  createProject: (data: import('../types/brownfield.types').CreateBrownfieldProjectRequest): Promise<ApiResponse<import('../types/brownfield.types').BrownfieldProject>> =>
    apiRequest<import('../types/brownfield.types').BrownfieldProject>('/brownfield/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Delete a project */
  deleteProject: (id: string): Promise<ApiResponse<{ deleted: boolean }>> =>
    apiRequest<{ deleted: boolean }>(`/brownfield/projects/${id}`, { method: 'DELETE' }),

  /** Trigger analysis (returns EventSource for SSE progress) */
  triggerAnalysis: (
    projectId: string,
    dimensions?: import('../types/brownfield.types').AnalysisDimension[],
  ): EventSource => {
    const url = `${API_BASE_URL}/brownfield/projects/${projectId}/analyze`;
    // We need to use fetch with SSE for POST, so we use a custom approach
    const eventSource = new EventSource(url);
    return eventSource;
  },

  /** Trigger analysis via fetch (for POST with body) */
  triggerAnalysisFetch: async (
    projectId: string,
    dimensions?: import('../types/brownfield.types').AnalysisDimension[],
    onProgress?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const token = localStorage.getItem('bringiq_auth_token');
    const response = await fetch(`${API_BASE_URL}/brownfield/projects/${projectId}/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ dimensions }),
    });

    if (!response.body) {
      onError?.('No response body');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const event = line.slice(7).trim();
          const dataLineIndex = lines.indexOf(line) + 1;
          if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith('data: ')) {
            try {
              const data = JSON.parse(lines[dataLineIndex].slice(6));
              if (event === 'progress') onProgress?.(data);
              else if (event === 'complete') onComplete?.(data);
              else if (event === 'error') onError?.(data.message);
            } catch { /* skip parse errors */ }
          }
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.message === 'Stream ended') break;
            onProgress?.(data);
          } catch { /* skip */ }
        }
      }
    }
  },

  /** Ask a question about the codebase (RAG query) */
  askCodebase: (
    projectId: string,
    query: string,
    maxResults?: number,
  ): Promise<ApiResponse<{ answer: string; sources: Array<{ file: string; snippet: string; relevance: number }> }>> =>
    apiRequest(`/brownfield/projects/${projectId}/ask`, {
      method: 'POST',
      body: JSON.stringify({ query, maxResults }),
    }),

  /** Get GitHub organizations */
  getGitHubOrgs: (): Promise<ApiResponse<import('../types/brownfield.types').GitHubAccount[]>> =>
    apiRequest<import('../types/brownfield.types').GitHubAccount[]>('/brownfield/github/orgs'),

  /** Search GitHub repositories */
  searchGitHubRepos: (
    owner?: string,
    search?: string,
  ): Promise<ApiResponse<import('../types/brownfield.types').GitHubRepoSearchResult[]>> => {
    const params = new URLSearchParams();
    if (owner) params.set('owner', owner);
    if (search) params.set('search', search);
    return apiRequest<import('../types/brownfield.types').GitHubRepoSearchResult[]>(
      `/brownfield/github/repos?${params.toString()}`,
    );
  },

  /** Get Confluence spaces for the export modal */
  getConfluenceSpaces: (): Promise<ApiResponse<Array<{ key: string; name: string }>>> =>
    apiRequest<Array<{ key: string; name: string }>>('/brownfield/confluence/spaces'),

  /** Export analysis results to Confluence as hierarchical pages */
  exportToConfluence: (
    projectId: string,
    spaceKey: string,
    parentTitle: string,
  ): Promise<ApiResponse<{
    parentPage: { id: string; title: string; url: string };
    childPages: Array<{ dimension: string; id: string; title: string; url: string }>;
    totalPages: number;
  }>> =>
    apiRequest(`/brownfield/projects/${projectId}/export/confluence`, {
      method: 'POST',
      body: JSON.stringify({ spaceKey, parentTitle }),
    }),

  // ─── Code Intelligence ───────────────────────────────────────────

  /** Extract Code DNA via SSE */
  extractCodeDNA: async (
    projectId: string,
    onProgress?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const token = localStorage.getItem('bringiq_auth_token');
    const response = await fetch(`${API_BASE_URL}/brownfield/projects/${projectId}/code-dna`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.body) { onError?.('No response body'); return; }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const event = line.slice(7).trim();
          const dataLineIndex = lines.indexOf(line) + 1;
          if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith('data: ')) {
            try {
              const data = JSON.parse(lines[dataLineIndex].slice(6));
              if (event === 'progress') onProgress?.(data);
              else if (event === 'complete') onComplete?.(data);
              else if (event === 'error') onError?.(data.message);
            } catch { /* skip */ }
          }
        }
      }
    }
  },

  /** Get extracted Code DNA */
  getCodeDNA: (projectId: string): Promise<ApiResponse<import('../types/code-intelligence.types').CodeDNA | null>> =>
    apiRequest(`/brownfield/projects/${projectId}/code-dna`),

  /** Generate AI artifacts via SSE */
  generateAIArtifacts: async (
    projectId: string,
    onProgress?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const token = localStorage.getItem('bringiq_auth_token');
    const response = await fetch(`${API_BASE_URL}/brownfield/projects/${projectId}/ai-artifacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.body) { onError?.('No response body'); return; }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const event = line.slice(7).trim();
          const dataLineIndex = lines.indexOf(line) + 1;
          if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith('data: ')) {
            try {
              const data = JSON.parse(lines[dataLineIndex].slice(6));
              if (event === 'progress') onProgress?.(data);
              else if (event === 'complete') onComplete?.(data);
              else if (event === 'error') onError?.(data.message);
            } catch { /* skip */ }
          }
        }
      }
    }
  },

  /** Get generated AI artifacts */
  getAIArtifacts: (projectId: string): Promise<ApiResponse<import('../types/code-intelligence.types').GeneratedAIArtifacts | null>> =>
    apiRequest(`/brownfield/projects/${projectId}/ai-artifacts`),

  /** Generate documentation via SSE */
  generateDocs: async (
    projectId: string,
    sections?: string[],
    onProgress?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const token = localStorage.getItem('bringiq_auth_token');
    const response = await fetch(`${API_BASE_URL}/brownfield/projects/${projectId}/generate-docs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ sections }),
    });

    if (!response.body) { onError?.('No response body'); return; }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const event = line.slice(7).trim();
          const dataLineIndex = lines.indexOf(line) + 1;
          if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith('data: ')) {
            try {
              const data = JSON.parse(lines[dataLineIndex].slice(6));
              if (event === 'progress') onProgress?.(data);
              else if (event === 'complete') onComplete?.(data);
              else if (event === 'error') onError?.(data.message);
            } catch { /* skip */ }
          }
        }
      }
    }
  },

  /** Get generated documentation */
  getGeneratedDocs: (projectId: string): Promise<ApiResponse<import('../types/code-intelligence.types').GeneratedDocumentation | null>> =>
    apiRequest(`/brownfield/projects/${projectId}/generated-docs`),

  /** Generate tests via SSE */
  generateTests: async (
    projectId: string,
    types?: ('unit' | 'integration' | 'e2e')[],
    targetFiles?: string[],
    onProgress?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const token = localStorage.getItem('bringiq_auth_token');
    const response = await fetch(`${API_BASE_URL}/brownfield/projects/${projectId}/generate-tests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ types, targetFiles }),
    });

    if (!response.body) { onError?.('No response body'); return; }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const event = line.slice(7).trim();
          const dataLineIndex = lines.indexOf(line) + 1;
          if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith('data: ')) {
            try {
              const data = JSON.parse(lines[dataLineIndex].slice(6));
              if (event === 'progress') onProgress?.(data);
              else if (event === 'complete') onComplete?.(data);
              else if (event === 'error') onError?.(data.message);
            } catch { /* skip */ }
          }
        }
      }
    }
  },

  /** Get generated tests */
  getGeneratedTests: (projectId: string): Promise<ApiResponse<import('../types/code-intelligence.types').GeneratedTestSuite | null>> =>
    apiRequest(`/brownfield/projects/${projectId}/generated-tests`),

  /** Process SDLC ticket via SSE */
  processTicket: async (
    projectId: string,
    ticket: import('../types/code-intelligence.types').SDLCTicketRequest,
    onProgress?: (data: any) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const token = localStorage.getItem('bringiq_auth_token');
    const response = await fetch(`${API_BASE_URL}/brownfield/projects/${projectId}/process-ticket`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(ticket),
    });

    if (!response.body) { onError?.('No response body'); return; }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          const event = line.slice(7).trim();
          const dataLineIndex = lines.indexOf(line) + 1;
          if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith('data: ')) {
            try {
              const data = JSON.parse(lines[dataLineIndex].slice(6));
              if (event === 'progress') onProgress?.(data);
              else if (event === 'complete') onComplete?.(data);
              else if (event === 'error') onError?.(data.message);
            } catch { /* skip */ }
          }
        }
      }
    }
  },
};

// ============================================================================
// Tags API
// ============================================================================

const tagsApi = {
  list: async (params?: {
    search?: string;
    color?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<import('../types').Tag>> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.color) queryParams.set('color', params.color);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    const queryString = queryParams.toString();
    return apiRequest(`/tags${queryString ? `?${queryString}` : ''}`) as Promise<PaginatedResponse<import('../types').Tag>>;
  },

  getById: async (id: string): Promise<ApiResponse<import('../types').Tag>> => {
    return apiRequest<import('../types').Tag>(`/tags/${id}`);
  },

  create: async (data: import('../types').CreateTagRequest): Promise<ApiResponse<import('../types').Tag>> => {
    return apiRequest<import('../types').Tag>('/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: string, data: import('../types').UpdateTagRequest): Promise<ApiResponse<import('../types').Tag>> => {
    return apiRequest<import('../types').Tag>(`/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiRequest<void>(`/tags/${id}`, {
      method: 'DELETE',
    });
  },

  tagResource: async (tagId: string, resourceId: string, resourceType: string): Promise<ApiResponse<import('../types').Tag>> => {
    return apiRequest<import('../types').Tag>(`/tags/${tagId}/resources`, {
      method: 'POST',
      body: JSON.stringify({ resourceId, resourceType }),
    });
  },

  untagResource: async (tagId: string, resourceId: string): Promise<ApiResponse<import('../types').Tag>> => {
    return apiRequest<import('../types').Tag>(`/tags/${tagId}/resources/${resourceId}`, {
      method: 'DELETE',
    });
  },
};

export default api;

// ============================================================================
// Billing API Types
// ============================================================================

export type PlanTier = 'free' | 'pro' | 'enterprise';
export type BillingCycle = 'monthly' | 'annual';

export interface PlanLimits {
  monthlyTokens: number;   // -1 = unlimited
  monthlyCost: number;     // -1 = unlimited
  maxAssistants: number;
  maxWorkflows: number;
  maxDatasourcesMb: number;
}

export interface PlanDefinition {
  id: PlanTier;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  limits: PlanLimits;
  features: string[];
}

export interface Subscription {
  id: string;
  userId: string;
  plan: PlanTier;
  status: string;
  billingCycle: BillingCycle;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface ModelUsageEntry {
  model: string;
  provider: string;
  tokens: number;
  cost: number;
  requests: number;
}

export interface UsageSummary {
  periodStart: string;
  periodEnd: string;
  serverKeyTokens: number;
  serverKeyCost: number;
  userKeyTokens: number;
  userKeyCost: number;
  totalRequests: number;
  plan: PlanTier;
  limits: PlanLimits;
  percentTokensUsed: number;
  percentCostUsed: number;
  isOverLimit: boolean;
  modelBreakdown: ModelUsageEntry[];
}

// ============================================================================
// Billing API
// ============================================================================

export const billingApi = {
  /** Get all available plan definitions */
  getPlans: (): Promise<ApiResponse<{ plans: PlanDefinition[] }>> =>
    apiRequest('/billing/plans'),

  /** Get the current user's subscription + plan details */
  getPlan: (): Promise<ApiResponse<{ subscription: Subscription; plan: PlanDefinition }>> =>
    apiRequest('/billing/plan'),

  /** Get current period usage vs plan limits */
  getUsage: (): Promise<ApiResponse<UsageSummary>> =>
    apiRequest('/billing/usage'),

  /** Upgrade to a new plan */
  upgradePlan: (
    plan: PlanTier,
    billingCycle: BillingCycle = 'monthly',
    stripePaymentMethodId?: string
  ): Promise<ApiResponse<{ subscription: Subscription; message: string }>> =>
    apiRequest('/billing/upgrade', {
      method: 'POST',
      body: JSON.stringify({ plan, billingCycle, stripePaymentMethodId }),
    }),
};
export { templateApi, skillsApi, meetingApi, developerAgentApi, brownfieldApi, tagsApi };
